// static/table_engine_src/core/esc_global.js
"use strict";

// Active engine is module-owned; other modules must access via getActiveEngine().
let __teActive = null;
let __teGlobalEscRefs = 0;
let __teGlobalEscHandler = null;

export function setActiveEngine(engine) {
  __teActive = engine || null;

  // Optional debug hook (safe). Comment out if you dislike globals.
  try {
    document.__teActiveEngine = __teActive;
  } catch {}
}

export function getActiveEngine() {
  return __teActive;
}

function isEditingInsideTable(table) {
  const ae = document.activeElement;
  if (!ae || !table) return false;
  if (!table.contains(ae)) return false;
  if (!ae.matches?.("input, textarea, select")) return false;
  // only treat as edit if it's inside tbody (header menus etc shouldn't count)
  return !!ae.closest?.("tbody");
}

function focusTableSoon(table) {
  if (!table) return;
  const run = () => {
    try {
      table.tabIndex = 0;
    } catch {}
    try {
      table.focus({ preventScroll: true });
    } catch {
      try {
        table.focus();
      } catch {}
    }
  };
  if (typeof queueMicrotask === "function") queueMicrotask(run);
  else Promise.resolve().then(run);
}

// fallback menu detection if a.isMenuOpen isn't wired correctly
function detectMenuOpen(table) {
  if (!table) return false;
  return !!table.querySelector(".te-context-menu, .te-menu, .te-dropdown, .te-popover");
}

export function installGlobalEscOnce() {
  if (typeof document === "undefined") return () => {};

  if (__teGlobalEscRefs === 0) {
    __teGlobalEscHandler = (e) => {
      if (e.key !== "Escape") return;

      const a = __teActive;
      const table = a?.table || null;
      if (!table) return;

      // If we're editing a cell, selection.js should handle ESC.
      // Do NOT interfere here.
      if (isEditingInsideTable(table)) return;

      // Only close menus/popovers if open.
      const menuOpen =
        (typeof a.isMenuOpen === "function" ? !!a.isMenuOpen() : false) ||
        detectMenuOpen(table);

      if (menuOpen) {
        e.preventDefault();
        e.stopPropagation();

        try {
          a.hideMenu?.();
        } catch {}

        // Key fix: restore focus after closing menu
        focusTableSoon(table);
        return;
      }

      // NAV mode: ESC should "do nothing".
      // But some browsers/UI states can drop focus to <body>/<html>;
      // if that happens, restore focus to the active table.
      const ae = document.activeElement;
      const lostFocus = !ae || ae === document.body || ae === document.documentElement;

      if (lostFocus) focusTableSoon(table);
    };

    document.addEventListener("keydown", __teGlobalEscHandler, true); // CAPTURE
    try {
      document.__teGlobalEscInstalled = true;
    } catch {}
  }

  __teGlobalEscRefs += 1;

  let released = false;
  return () => {
    if (released) return;
    released = true;

    __teGlobalEscRefs = Math.max(0, __teGlobalEscRefs - 1);
    if (__teGlobalEscRefs !== 0) return;

    if (__teGlobalEscHandler) {
      try {
        document.removeEventListener("keydown", __teGlobalEscHandler, true);
      } catch {}
    }
    __teGlobalEscHandler = null;

    try {
      delete document.__teGlobalEscInstalled;
    } catch {
      try {
        document.__teGlobalEscInstalled = false;
      } catch {}
    }

    if (__teActive && !__teActive.table) __teActive = null;
  };
}
